import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAzureBackupCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { BackupInstance, BackupWorkloadType, BackupRedundancy, AzureBackupRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const WORKLOAD_OPTIONS: { value: BackupWorkloadType; label: string; changeRate: string }[] = [
  { value: 'vm',           label: 'Azure VM',             changeRate: '5% daily change' },
  { value: 'sql-in-vm',   label: 'SQL Server in Azure VM', changeRate: '10% daily change' },
  { value: 'azure-files', label: 'Azure Files',           changeRate: '2% daily change' },
]

const REDUNDANCY_OPTIONS: { value: BackupRedundancy; label: string; description: string }[] = [
  { value: 'lrs',    label: 'LRS',    description: 'Locally Redundant Storage — 3 copies in one datacenter' },
  { value: 'zrs',    label: 'ZRS',    description: 'Zone Redundant — 3 copies across availability zones' },
  { value: 'grs',    label: 'GRS',    description: 'Geo-Redundant — 6 copies across 2 regions (~2× LRS cost)' },
  { value: 'ra-grs', label: 'RA-GRS', description: 'Read-Access GRS — geo-redundant + read from secondary (~2.5× LRS cost)' },
]

const DEFAULT_INSTANCE: BackupInstance = { workloadType: 'vm', activeDataGb: 100, retentionDays: 30 }

function InstanceRow({
  instance, index, onUpdate, onRemove, canRemove
}: {
  instance: BackupInstance
  index: number
  onUpdate: (i: BackupInstance) => void
  onRemove: () => void
  canRemove: boolean
}) {
  return (
    <div className="p-4 rounded-lg space-y-3" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-semibold" style={{ color: 'var(--text-tertiary)' }}>Instance {index + 1}</span>
        {canRemove && (
          <button onClick={onRemove} className="text-[12px] flex items-center gap-1 px-2 py-1 rounded" style={{ color: '#a4262c', border: '1px solid rgba(164,38,44,0.3)' }}>
            <span className="material-symbols-outlined text-[14px]">remove</span> Remove
          </button>
        )}
      </div>

      {/* Workload type */}
      <div>
        <label className="text-[12px] font-medium block mb-1" style={{ color: 'var(--text-secondary)' }}>Workload type</label>
        <select
          value={instance.workloadType}
          onChange={(e) => onUpdate({ ...instance, workloadType: e.target.value as BackupWorkloadType })}
          className="w-full rounded px-3 py-2 text-[13px] border"
          style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
        >
          {WORKLOAD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label} — {o.changeRate}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Active data */}
        <div>
          <label className="flex items-center text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Active data (GB)
            <FieldTooltip content="The size of the data being protected. Used to estimate vault storage via the daily change rate for your workload type." />
          </label>
          <input
            type="number" value={instance.activeDataGb} min={1} max={100000} step={10}
            onChange={(e) => onUpdate({ ...instance, activeDataGb: Math.max(1, Number(e.target.value)) })}
            className="w-full rounded px-3 py-2 text-[13px] border"
            style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
          />
        </div>

        {/* Retention */}
        <div>
          <label className="flex items-center text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Retention (days)
            <FieldTooltip content="Longer retention = more vault storage. Each extra retained day adds daily-change-rate × active data GB to your vault. 365-day retention typically means 6–20× the active data size stored." />
          </label>
          <input
            type="number" value={instance.retentionDays} min={7} max={3650} step={7}
            onChange={(e) => onUpdate({ ...instance, retentionDays: Math.max(7, Math.min(3650, Number(e.target.value))) })}
            className="w-full rounded px-3 py-2 text-[13px] border"
            style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
          />
        </div>
      </div>
    </div>
  )
}

export default function AzureBackupCalc() {
  const [instances, setInstances] = useState<BackupInstance[]>([{ ...DEFAULT_INSTANCE }])
  const [redundancy, setRedundancy] = useState<BackupRedundancy>('lrs')

  const inputs: AzureBackupRequest = { instances, redundancy }
  const { data: result, isLoading, isError } = useAzureBackupCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (result?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'azure-backup', result_gbp: result.monthly_estimate })
    }
  }, [result?.monthly_estimate])

  const addInstance = () => {
    if (instances.length < 100) setInstances(prev => [...prev, { ...DEFAULT_INSTANCE }])
  }

  const updateInstance = (i: number, updated: BackupInstance) => {
    setInstances(prev => prev.map((inst, idx) => idx === i ? updated : inst))
  }

  const removeInstance = (i: number) => {
    setInstances(prev => prev.filter((_, idx) => idx !== i))
  }

  const isGeoRedundant = redundancy === 'grs' || redundancy === 'ra-grs'

  return (
    <div className="flex flex-col gap-8">
      <TrustBar />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        {/* Left column: configuration */}
        <div className="space-y-6">
          <ScenarioPresets presets={[
        {
          label: 'VM backup (small)',
          description: 'LRS · 3 VMs · 50 GB each — most common Backup use case',
          apply: () => { setRedundancy('lrs'); setInstances([{ workloadType: 'vm', activeDataGb: 50, retentionDays: 30 }, { workloadType: 'vm', activeDataGb: 50, retentionDays: 30 }, { workloadType: 'vm', activeDataGb: 50, retentionDays: 30 }]); },
        },
        {
          label: 'VM backup (enterprise)',
          description: 'GRS · 20 VMs · 200 GB each — shows geo-redundancy cost premium at scale',
          apply: () => { setRedundancy('grs'); setInstances(Array.from({ length: 20 }, () => ({ workloadType: 'vm' as BackupWorkloadType, activeDataGb: 200, retentionDays: 30 }))); },
        },
      ]} />

      {/* Storage-dominates trap — headline warning */}
      <div className="p-4 rounded-lg flex gap-3" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.35)' }}>
        <span className="material-symbols-outlined text-[#e8b000] text-[20px] shrink-0 mt-0.5">info</span>
        <div>
          <p className="text-[13px] font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>Storage usually costs more than the instance fee</p>
          <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
            Azure Backup pricing shows a per-instance fee and a small "plus storage" note. In practice, backup vault storage is typically 3–5× the instance fee. This calculator shows both — watch the storage line in the breakdown.
          </p>
        </div>
      </div>

      {/* Instance list */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Protected instances</h2>

        <div className="space-y-3">
          {instances.map((inst, i) => (
            <InstanceRow
              key={i}
              index={i}
              instance={inst}
              onUpdate={(updated) => updateInstance(i, updated)}
              onRemove={() => removeInstance(i)}
              canRemove={instances.length > 1}
            />
          ))}
        </div>

        {instances.length < 100 && (
          <button
            onClick={addInstance}
            className="flex items-center gap-2 px-4 py-2 rounded text-[13px] font-medium transition-colors"
            style={{ border: '1px dashed var(--outline-variant)', color: '#0078d4' }}
          >
            <span className="material-symbols-outlined text-[16px]">add</span>
            Add instance
          </button>
        )}
      </div>

      {/* Redundancy selector */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>
          Vault storage redundancy
          <FieldTooltip content="Redundancy applies to all backup vault storage. GRS/RA-GRS cost ~2–2.5× LRS. Only use geo-redundant options if cross-region restore from a regional failure is a documented requirement." />
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {REDUNDANCY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRedundancy(opt.value)}
              className="p-3 rounded text-left transition-colors"
              style={{
                border: redundancy === opt.value ? '2px solid #0078d4' : '1px solid var(--outline-variant)',
                backgroundColor: redundancy === opt.value ? 'rgba(0,120,212,0.06)' : 'var(--bg-surface)',
              }}
            >
              <p className="text-[13px] font-semibold" style={{ color: redundancy === opt.value ? '#0078d4' : 'var(--text-primary)' }}>{opt.label}</p>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{opt.description}</p>
            </button>
          ))}
        </div>

        {isGeoRedundant && (
          <div className="p-3 rounded flex gap-2" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.3)' }}>
            <span className="material-symbols-outlined text-[#e8b000] text-[16px] shrink-0 mt-0.5">warning</span>
            <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              {redundancy === 'ra-grs' ? 'RA-GRS costs ~2.5×' : 'GRS costs ~2×'} LRS storage. Use only if cross-region durability is a documented requirement. Dev/test workloads should use LRS.
            </p>
          </div>
        )}
        </div>{/* end redundancy section */}
        </div>{/* end left column */}

        {/* Right column: results panel */}
        <div className="sticky top-20 self-start space-y-4">
      {isLoading && (
        <div className="p-5 rounded-lg text-center text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Calculating…
        </div>
      )}
      {isError && (
        <div className="p-5 rounded-lg text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: '#a4262c' }}>
          Could not fetch pricing. Check your inputs and try again.
        </div>
      )}
      {result && !isLoading && (
        <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Estimated monthly cost</h2>
            <span className="text-[28px] md:text-[36px] font-bold" style={{ color: '#0078d4' }}>£{fmt(result.monthly_estimate)}</span>
          </div>

          {/* Breakdown — storage line is visually prominent */}
          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
            {/* Instance fees */}
            <div className="flex justify-between text-[13px] gap-2">
              <span style={{ color: 'var(--text-secondary)' }}>
                Protected instance fees ({instances.length} instance{instances.length !== 1 ? 's' : ''})
              </span>
              <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.totalInstanceFees)}</span>
            </div>

            {/* Storage — visually prominent */}
            <div
              className="flex justify-between items-center gap-2 px-3 py-2 rounded"
              style={{ backgroundColor: result.storageDominates ? 'rgba(232,176,0,0.08)' : 'rgba(0,120,212,0.05)', border: `1px solid ${result.storageDominates ? 'rgba(232,176,0,0.35)' : 'rgba(0,120,212,0.2)'}` }}
            >
              <div>
                <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Backup vault storage — {result.totalVaultGb.toFixed(0)} GB ({redundancy.toUpperCase()})
                </p>
                <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  {result.storagePct}% of total cost
                  {result.storageDominates && ' — storage dominates this estimate'}
                </p>
              </div>
              <span className="text-[15px] font-bold shrink-0" style={{ color: result.storageDominates ? '#e8b000' : '#0078d4' }}>
                £{fmt(result.totalStorageCost)}
              </span>
            </div>

            <div className="flex justify-between text-[13px] font-semibold pt-1 gap-2" style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}>
              <span>Total</span>
              <span>£{fmt(result.monthly_estimate)}/month</span>
            </div>
          </div>

          {/* Per-instance table (collapsed if only 1) */}
          {result.instanceRows && result.instanceRows.length > 1 && (
            <div className="pt-2 space-y-1" style={{ borderTop: '1px solid var(--outline-variant)' }}>
              <p className="text-[12px] font-semibold mb-2" style={{ color: 'var(--text-tertiary)' }}>Per-instance breakdown</p>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <th className="text-left pb-1.5 font-medium pr-3" style={{ color: 'var(--text-tertiary)' }}>#</th>
                      <th className="text-left pb-1.5 font-medium pr-3" style={{ color: 'var(--text-tertiary)' }}>Type</th>
                      <th className="text-right pb-1.5 font-medium pr-3" style={{ color: 'var(--text-tertiary)' }}>Instance fee</th>
                      <th className="text-right pb-1.5 font-medium pr-3" style={{ color: 'var(--text-tertiary)' }}>Est. vault GB</th>
                      <th className="text-right pb-1.5 font-medium" style={{ color: 'var(--text-tertiary)' }}>Storage cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.instanceRows.map((row) => (
                      <tr key={row.index} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                        <td className="py-1.5 pr-3" style={{ color: 'var(--text-tertiary)' }}>{row.index}</td>
                        <td className="py-1.5 pr-3" style={{ color: 'var(--text-secondary)' }}>{row.label}</td>
                        <td className="py-1.5 pr-3 text-right" style={{ color: 'var(--text-primary)' }}>£{fmt(row.instanceFee)}</td>
                        <td className="py-1.5 pr-3 text-right" style={{ color: 'var(--text-secondary)' }}>{row.estimatedVaultGb.toFixed(0)} GB</td>
                        <td className="py-1.5 text-right" style={{ color: 'var(--text-primary)' }}>£{fmt(row.storageCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Redundancy comparison */}
          {result.redundancyComparison && (
            <div className="p-3 rounded text-[13px] space-y-1.5" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Storage redundancy comparison</p>
              {result.redundancyComparison.map((row) => (
                <div key={row.redundancy} className="flex justify-between items-center">
                  <span style={{ color: row.isSelected ? '#0078d4' : 'var(--text-secondary)', fontWeight: row.isSelected ? 600 : 400 }}>
                    {row.label}
                    {row.isSelected && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,120,212,0.1)', color: '#0078d4' }}>selected</span>}
                  </span>
                  <span style={{ color: row.isSelected ? '#0078d4' : 'var(--text-primary)' }}>£{fmt(row.totalMonthly)}/mo</span>
                </div>
              ))}
            </div>
          )}

          {/* Formula disclosure */}
          <div className="p-3 rounded text-[11px] space-y-1" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
            <p className="font-semibold" style={{ color: 'var(--text-secondary)' }}>How vault storage is estimated</p>
            <p>Vault GB = activeDataGb × (1 + dailyChangeRate × retentionDays)</p>
            <p>Daily change rates: VM 5%, SQL Server 10%, Azure Files 2%. These are approximations — actual consumption depends on deduplication, compression, and workload volatility.</p>
          </div>

          {result.warnings?.map((w: string) => (
            <div key={w} className="flex gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">info</span>
              <span>{w}</span>
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <ShareButton inputs={inputs} result={result} service="azure-backup" />
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/backup/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-1" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>

          <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{result.disclaimer}</p>
        </div>
      )}
        </div>{/* end right column */}
      </div>{/* end grid */}

      {/* Billing traps */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[16px] align-middle mr-1">warning</span>
          Azure Backup billing traps
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'Storage dominates the instance fee',
              body: 'The protected instance fee looks small — £7.55/VM/month. But vault storage for a 500 GB VM with 30-day retention is typically ~7.5× that. Teams that budget only the instance fee are surprised when the first invoice arrives.',
            },
            {
              title: 'GRS / RA-GRS is ~2× LRS',
              body: 'Geo-redundant storage is often enabled by default in deployment templates. For dev/test workloads that will never need cross-region restore, this doubles your storage cost for no operational benefit.',
            },
            {
              title: 'Retention multiplies vault storage',
              body: 'Extending retention from 30 to 365 days can increase vault storage by 10–18× (depending on daily change rate). A 12-month retention policy for SQL workloads with 10% daily change rate stores ~37× the active data size.',
            },
            {
              title: 'Soft-delete adds 14 days beyond policy',
              body: 'Soft-delete is enabled by default and protects against accidental deletion. It keeps data for 14 days after the retention policy expires. For large vaults, this adds a meaningful storage tail beyond your intended retention window.',
            },
          ].map(trap => (
            <div key={trap.title} className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{trap.title}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{trap.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cross-links */}
      <div className="space-y-2">
        <Link
          to="/site-recovery"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>cloud_sync</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Site Recovery calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Continuous replication + failover for disaster recovery — same storage-dominates cost shape</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
        <Link
          to="/managed-disks"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>storage</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Managed Disks calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Estimate the disk storage your VMs are running on — adjacent to backup cost planning</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
